import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import CTASection from "@/components/public/CTASection";
import { Award, Clock, Leaf, MapPin, Target, Users } from "lucide-react";
import SeoHead from "@/components/public/SeoHead";
import Breadcrumb from "@/components/public/Breadcrumb";
import FadeIn from "@/components/public/FadeIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AProposPage() {
  const [villes, setVilles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/villes`).then(r => setVilles(r.data));
  }, []);

  return (
    <div data-testid="a-propos-page">
      <SeoHead
        title="À propos de Nettoyage Expert"
        description="Nettoyage Expert : +500 interventions en Suisse romande. Équipement professionnel, produits écologiques. Noté 5★ Google. Basé à Yverdon-les-Bains."
        path="/a-propos"
        breadcrumbs={[{ name: "Accueil", url: "/" }, { name: "À propos" }]}
      />
      <Breadcrumb items={[{ name: "Accueil", url: "/" }, { name: "À propos" }]} />
      {/* Hero */}
      <section className="bg-[#336DBF] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
            À propos de Nettoyage Expert
          </h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">
            Depuis notre création, nous avons un seul objectif : fournir des résultats visibles et durables à chaque intervention.
          </p>
          <Link to="/contact" className="mt-6 inline-flex items-center justify-center px-8 py-3.5 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E] transition-colors" data-testid="about-hero-cta">
            Nous contacter
          </Link>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Notre histoire</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
              Une entreprise nee de la passion du travail bien fait
            </h2>
            <div className="text-[#062133]/60 text-lg leading-relaxed space-y-4">
              <p>
                Nettoyage Expert est né d'un constat simple : trop d'entreprises de nettoyage en Suisse romande proposent un service médiocre. Des résultats invisibles, des produits inadaptés, des équipes non formées.
              </p>
              <p>
                Nous avons décidé de faire les choses différemment. Chaque membre de notre équipe est formé aux techniques professionnelles les plus avancées. Nous investissons dans du matériel de dernière génération — injection-extraction, nettoyage haute pression, traitement hydrofuge — parce que le résultat dépend de l'outil.
              </p>
              <p>
                Basés à Yverdon-les-Bains, nous intervenons dans tout le canton de Vaud, à Genève, Fribourg, Neuchâtel et en Valais. Plus de 500 interventions réalisées, et un engagement : le résultat que vous attendez, ou nous revenons.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Nos valeurs</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight">Ce qui nous anime</h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {[
              { icon: Award, title: "Qualité", desc: "Nous ne faisons aucun compromis sur la qualité. Chaque intervention est réalisée avec du matériel professionnel et des produits certifiés." },
              { icon: Clock, title: "Rapidité", desc: "Devis sous 2 heures, intervention sous 48h. Votre temps est précieux, nous respectons nos engagements." },
              { icon: Target, title: "Résultat garanti", desc: "Si le résultat ne vous satisfait pas, nous revenons gratuitement. C'est notre engagement qualité." },
              { icon: Leaf, title: "Respect de l'environnement", desc: "Produits écologiques, techniques à faible consommation d'eau, gestion responsable des déchets." },
            ].map((v, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="bg-white border border-[#062133]/10 rounded-lg p-6">
                  <v.icon className="w-8 h-8 text-[#336DBF] mb-4" />
                  <h3 className="font-semibold text-[#062133] mb-2">{v.title}</h3>
                  <p className="text-sm text-[#062133]/60 leading-relaxed">{v.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Vous méritez un nettoyage professionnel" subtitle="Plus de 500 interventions réalisées en Suisse romande." />

      {/* Zones */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Couverture</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-8">Zones d'intervention</h2>
          </FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {villes.map((v, i) => (
              <FadeIn key={v.slug} delay={i * 80}>
                <div className="bg-[#F4F6F8] rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={16} className="text-[#336DBF]" />
                    <h3 className="font-semibold text-[#062133]">{v.name}</h3>
                  </div>
                  <p className="text-xs text-[#062133]/60">{v.canton}</p>
                  <p className="text-xs text-[#062133]/60 mt-1">{v.quartiers?.join(", ")}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <div className="mt-8">
            <p className="text-sm text-[#062133]/60">
              <strong>Cantons desservis :</strong> Vaud, Genève, Fribourg, Neuchâtel, Valais
            </p>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Notre équipe</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">Des professionnels à votre service</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              {[
                { num: "500+", label: "Interventions réalisées" },
                { num: "8", label: "Villes couvertes" },
                { num: "5/5", label: "Note moyenne clients" },
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-lg p-6 border border-[#062133]/10">
                  <p className="text-3xl font-bold text-[#336DBF]">{stat.num}</p>
                  <p className="text-sm text-[#062133]/60 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Notre organisation */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Notre organisation</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">Un écosystème digital au service de la qualité</h2>
            <div className="space-y-4 text-[#062133]/70 leading-relaxed">
              <p>
                Chez Nettoyage Expert, aucun appel ne se perd : ils sont pris en charge 24h/24 par{" "}
                <a href="https://talk-flow.ai" target="_blank" rel="noopener" className="text-[#336DBF] font-medium hover:underline">Talk-Flow</a>,
                notre agent téléphonique IA, puis gérés dans{" "}
                <a href="https://www.loomy-crm.com" target="_blank" rel="noopener" className="text-[#336DBF] font-medium hover:underline">Loomy CRM</a>{" "}
                pour la prise de rendez-vous. Notre CRM de gestion client nous permet aussi de relancer nos clients par e-mail, WhatsApp et via notre bot vocal, et d'établir devis et factures en ligne — et bien plus encore. Côté visibilité, nous avons un partenariat avec{" "}
                <a href="https://www.viralya.io" target="_blank" rel="noopener" className="text-[#336DBF] font-medium hover:underline">Viralya</a>{" "}
                qui gère nos réseaux sociaux et notre influenceur IA.
              </p>
              <p>
                Toute cette infrastructure a été conçue et mise en place par{" "}
                <a href="https://darven.ch" target="_blank" rel="noopener" className="text-[#336DBF] font-medium hover:underline">Darven</a>,
                qui gère également notre acquisition client.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <CTASection title="Prêt à travailler avec nous ?" buttonText="Contactez-nous" buttonLink="/contact" />
    </div>
  );
}
